import React, { useState, useEffect } from 'react';
import { useParentPortal } from '../contexts/ParentPortalContext';
import { useFamilyContext } from '../../../../contexts/FamilyContext';
import { supabase } from '../../../../lib/supabase';
import { X, Save, CheckCircle, AlertCircle, Clock, User } from 'lucide-react';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const StudentProfileModal = ({ isOpen, onClose }) => {
  const { selectedStudent } = useParentPortal();
  const { currentUser, family } = useFamilyContext();
  const [profile, setProfile] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && selectedStudent) {
      loadProfile();
    }
  }, [isOpen, selectedStudent]);

  const loadProfile = async () => {
    if (!selectedStudent) return;

    setLoading(true);
    try {
      const studentId = selectedStudent.auth_user_id || selectedStudent.id;
      // Load from the student_academic_profiles table
      const { data, error } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', studentId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading profile:', error);
      } else if (data) {
        // Map database fields to our component state
        const mappedProfile = {
          currentGpa: data.gpa || data.current_gpa || '',
          classRank: data.class_rank || '',
          graduationYear: data.graduation_year || '',
          satTotal: data.sat_total || '',
          actComposite: data.act_composite || '',
          psatTotal: data.psat_total || '',
          academicInterests: data.academic_interests || '',
          careerGoals: data.career_goals || '',
          highSchoolState: data.high_school_state || '',
          highSchoolType: data.high_school_type || '',
          curriculumRigor: data.curriculum_rigor || '',
          firstGenerationCollege: data.first_generation_college,
          legacyConnections: data.legacy_connections || '',
          specialCircumstances: data.special_circumstances || ''
        };
        setProfile(mappedProfile);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const saveProfile = async () => {
    if (!selectedStudent) return;

    setSaveStatus('saving');
    setSaveMessage('');

    try {
      const studentId = selectedStudent.auth_user_id || selectedStudent.id;
      // Map component state to database fields
      const updateData = {
        user_id: studentId,
        gpa: profile.currentGpa || null,
        class_rank: profile.classRank || null,
        graduation_year: profile.graduationYear ? parseInt(profile.graduationYear) : null,
        sat_total: profile.satTotal ? parseInt(profile.satTotal) : null,
        act_composite: profile.actComposite ? parseInt(profile.actComposite) : null,
        psat_total: profile.psatTotal ? parseInt(profile.psatTotal) : null,
        academic_interests: profile.academicInterests || null,
        career_goals: profile.careerGoals || null,
        high_school_state: profile.highSchoolState || null,
        high_school_type: profile.highSchoolType || null,
        curriculum_rigor: profile.curriculumRigor || null,
        first_generation_college: profile.firstGenerationCollege || false,
        legacy_connections: profile.legacyConnections || null,
        special_circumstances: profile.specialCircumstances || null,
        updated_at: new Date().toISOString()
      };

      // Save to the student_academic_profiles table
      const { error } = await supabase
        .from('student_academic_profiles')
        .upsert(updateData, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setSaveStatus('success');
      setSaveMessage('Student profile saved successfully!');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveStatus('error');
      setSaveMessage('Failed to save profile. Please try again.');
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-azure-100 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-azure-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                {selectedStudent?.name || 'Student'} Academic Profile
              </h2>
              <p className="text-sm text-slate-600">Comprehensive academic and background information</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {loading ? (
            <div className="animate-pulse space-y-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="space-y-4">
                  <div className="h-6 bg-slate-200 rounded w-1/4"></div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="h-10 bg-slate-200 rounded"></div>
                    <div className="h-10 bg-slate-200 rounded"></div>
                    <div className="h-10 bg-slate-200 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Basic Academic Information */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4">Academic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Current GPA
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={profile.currentGpa || ''}
                      onChange={(e) => updateProfile('currentGpa', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                      placeholder="3.85"
                      min="0"
                      max="4.0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Class Rank
                    </label>
                    <input
                      type="text"
                      value={profile.classRank || ''}
                      onChange={(e) => updateProfile('classRank', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                      placeholder="15 of 450"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Graduation Year
                    </label>
                    <input
                      type="number"
                      value={profile.graduationYear || ''}
                      onChange={(e) => updateProfile('graduationYear', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                      placeholder="2025"
                    />
                  </div>
                </div>
              </div>

              {/* Test Scores */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4">Standardized Test Scores</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      SAT Total Score
                    </label>
                    <input
                      type="number"
                      value={profile.satTotal || ''}
                      onChange={(e) => updateProfile('satTotal', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                      placeholder="1450"
                      min="400"
                      max="1600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      ACT Composite Score
                    </label>
                    <input
                      type="number"
                      value={profile.actComposite || ''}
                      onChange={(e) => updateProfile('actComposite', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                      placeholder="32"
                      min="1"
                      max="36"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      PSAT Total Score
                    </label>
                    <input
                      type="number"
                      value={profile.psatTotal || ''}
                      onChange={(e) => updateProfile('psatTotal', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                      placeholder="1420"
                      min="320"
                      max="1520"
                    />
                  </div>
                </div>
              </div>

              {/* Academic Interests & Goals */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4">Academic Interests & Goals</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Academic Interests
                    </label>
                    <textarea
                      value={profile.academicInterests || ''}
                      onChange={(e) => updateProfile('academicInterests', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                      rows="3"
                      placeholder="STEM, Liberal Arts, Business, Fine Arts..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Career Goals
                    </label>
                    <textarea
                      value={profile.careerGoals || ''}
                      onChange={(e) => updateProfile('careerGoals', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                      rows="3"
                      placeholder="Software engineer, doctor, teacher, researcher..."
                    />
                  </div>
                </div>
              </div>

              {/* High School Details */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4">High School Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      High School State
                    </label>
                    <select
                      value={profile.highSchoolState || ''}
                      onChange={(e) => updateProfile('highSchoolState', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                    >
                      <option value="">Select state...</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      High School Type
                    </label>
                    <select
                      value={profile.highSchoolType || ''}
                      onChange={(e) => updateProfile('highSchoolType', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                    >
                      <option value="">Select type...</option>
                      <option value="public">Public</option>
                      <option value="private">Private</option>
                      <option value="charter">Charter</option>
                      <option value="magnet">Magnet</option>
                      <option value="homeschool">Homeschool</option>
                      <option value="online">Online</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Curriculum Rigor
                    </label>
                    <select
                      value={profile.curriculumRigor || ''}
                      onChange={(e) => updateProfile('curriculumRigor', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                    >
                      <option value="">Select rigor level</option>
                      <option value="most_rigorous">Most Rigorous</option>
                      <option value="very_rigorous">Very Rigorous</option>
                      <option value="rigorous">Rigorous</option>
                      <option value="moderately_rigorous">Moderately Rigorous</option>
                      <option value="less_rigorous">Less Rigorous</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Background & Special Circumstances */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4">Background & Special Circumstances</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      First Generation College Student
                    </label>
                    <select
                      value={profile.firstGenerationCollege || ''}
                      onChange={(e) => updateProfile('firstGenerationCollege', e.target.value === 'true')}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                    >
                      <option value="">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Legacy Connections
                    </label>
                    <textarea
                      value={profile.legacyConnections || ''}
                      onChange={(e) => updateProfile('legacyConnections', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                      rows="2"
                      placeholder="Parent attended Harvard, Sibling at Duke..."
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Special Circumstances
                    </label>
                    <textarea
                      value={profile.specialCircumstances || ''}
                      onChange={(e) => updateProfile('specialCircumstances', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                      rows="3"
                      placeholder="Family challenges, medical issues, unique background information..."
                    />
                  </div>
                </div>
              </div>

              {/* Save Section */}
              <div className="pt-6 border-t border-slate-200">
                {saveStatus && (
                  <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                    saveStatus === 'success' ? 'bg-emerald-50 border border-emerald-200' :
                    saveStatus === 'error' ? 'bg-red-50 border border-red-200' :
                    'bg-azure-50 border border-azure-200'
                  }`}>
                    {saveStatus === 'success' && <CheckCircle className="w-5 h-5 text-emerald-600" />}
                    {saveStatus === 'error' && <AlertCircle className="w-5 h-5 text-red-600" />}
                    {saveStatus === 'saving' && <Clock className="w-5 h-5 text-azure-600 animate-spin" />}
                    <span className={
                      saveStatus === 'success' ? 'text-emerald-700' :
                      saveStatus === 'error' ? 'text-red-700' :
                      'text-azure-700'
                    }>
                      {saveMessage || (saveStatus === 'saving' ? 'Saving profile...' : '')}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    onClick={saveProfile}
                    disabled={saveStatus === 'saving'}
                    className="bg-azure-600 hover:bg-azure-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
                  >
                    {saveStatus === 'saving' ? (
                      <>
                        <Clock className="w-5 h-5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Save Profile
                      </>
                    )}
                  </button>
                  <button
                    onClick={onClose}
                    className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentProfileModal;